// src/pages/MemberDividends.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DateRangeIcon from "@mui/icons-material/DateRange";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { fetchMyDividendPayouts } from "../services/dividendService";
import { useTranslation } from "react-i18next";

const brand = "#800b33";
const brandMuted = "#c15378";

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "TZS 0";
  return `TZS ${new Intl.NumberFormat("en-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const StatCard = ({ icon, title, subtitle, accent }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 4,
        height: "100%",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              backgroundColor: `${accent}20`,
              color: accent,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  </motion.div>
);

const EmptyState = ({ title, message }) => (
  <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <Typography variant="body2">
      {message}
    </Typography>
  </Box>
);

const MemberDividends = () => {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    from: "",
    to: "",
    target: "ALL",
  });

  const loadPayouts = async () => {
    setLoading(true);
    try {
      const data = await fetchMyDividendPayouts();
      setPayouts(data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("Failed to load dividends"),
        text: error.message,
        confirmButtonColor: brand,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayouts();
  }, []);

  const filteredPayouts = useMemo(() => {
    return payouts.filter((item) => {
      const matchesQuery =
        !filters.query ||
        item.run_description?.toLowerCase().includes(filters.query.toLowerCase()) ||
        formatDate(item.run_period_start).toLowerCase().includes(filters.query.toLowerCase()) ||
        formatDate(item.run_period_end).toLowerCase().includes(filters.query.toLowerCase());

      const matchesTarget =
        filters.target === "ALL" || item.run_target_type === filters.target;

      const payoutDate = item.created_at ? new Date(item.created_at) : null;
      const matchesFrom = !filters.from || (payoutDate && payoutDate >= new Date(filters.from));
      const matchesTo = !filters.to || (payoutDate && payoutDate <= new Date(filters.to));

      return matchesQuery && matchesTarget && matchesFrom && matchesTo;
    });
  }, [payouts, filters]);

  const totals = useMemo(() => {
    if (!payouts.length) {
      return {
        total: 0,
        latest: null,
        largest: 0,
      };
    }
    return {
      total: payouts.reduce((sum, item) => sum + Number(item.amount), 0),
      latest: payouts[0],
      largest: payouts.reduce((max, item) => Math.max(max, Number(item.amount)), 0),
    };
  }, [payouts]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={900} color={brand}>
            {t("My Dividends")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Track your personal dividend receipts with bank-grade transparency.")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadPayouts}
          disabled={loading}
          sx={{ bgcolor: brand, "&:hover": { bgcolor: "#5f0824" } }}
        >
          {t("Refresh")}
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<SavingsOutlinedIcon fontSize="large" />}
            title={t("Total dividends received")}
            subtitle={formatCurrency(totals.total)}
            accent={brand}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<DateRangeIcon fontSize="large" />}
            title={t("Last distribution")}
            subtitle={
              totals.latest ? `${formatCurrency(totals.latest.amount)} · ${formatDate(totals.latest.created_at)}` : t("Pending")
            }
            accent={brandMuted}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<TrendingUpIcon fontSize="large" />}
            title={t("Largest single payout")}
            subtitle={formatCurrency(totals.largest)}
            accent="#009688"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t("Payout History")}
              </Typography>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("Search description or period...")}
                    value={filters.query}
                    onChange={(e) => handleFilterChange("query", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("From")}
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.from}
                    onChange={(e) => handleFilterChange("from", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("To")}
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.to}
                    onChange={(e) => handleFilterChange("to", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("Target Type")}</InputLabel>
                    <Select
                      label={t("Target Type")}
                      value={filters.target}
                      onChange={(e) => handleFilterChange("target", e.target.value)}
                    >
                      <MenuItem value="ALL">{t("All Runs")}</MenuItem>
                      <MenuItem value="ACTIVE">{t("Active Members")}</MenuItem>
                      <MenuItem value="BORROWERS">{t("Borrowers")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Period")}</TableCell>
                      <TableCell>{t("Target")}</TableCell>
                      <TableCell>{t("Description")}</TableCell>
                      <TableCell align="right">{t("Amount")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && filteredPayouts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <EmptyState
                            title={t("No dividend payouts yet")}
                            message={t("Your dividend earnings will appear here right after the next group distribution.")}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading &&
                      filteredPayouts.map((payout) => (
                        <TableRow key={payout.id} hover>
                          <TableCell>
                            {formatDate(payout.run_period_start)} – {formatDate(payout.run_period_end)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t(payout.run_target_type)}
                              size="small"
                              sx={{ bgcolor: `${brand}10`, color: brand, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            {payout.run_description || t("Dividend distribution")}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t("Posted on {{date}}", { date: formatDate(payout.created_at) })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(payout.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 4, height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t("Latest Activity")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {filteredPayouts.slice(0, 6).map((payout) => (
                <Box
                  key={payout.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #eee",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={600}>{formatCurrency(payout.amount)}</Typography>
                    <Chip
                      label={t(payout.run_target_type)}
                      size="small"
                      sx={{ bgcolor: "#fff", border: "1px solid #ddd" }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatDate(payout.run_period_start)} – {formatDate(payout.run_period_end)}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <MonetizationOnIcon fontSize="small" sx={{ color: brand }} />
                    <Typography variant="caption">
                      {t("Credited on {{date}}", { date: formatDate(payout.created_at) })}
                    </Typography>
                  </Stack>
                </Box>
              ))}
              {!filteredPayouts.length && !loading && (
                <EmptyState
                  title={t("No dividend payouts yet")}
                  message={t("Your dividend earnings will appear here right after the next group distribution.")}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemberDividends;
